// SPDX-License-Identifier: AGPL-3.0-or-later

import {LongPressable} from '@app/features/app/components/LongPressable';
import {usePressable} from '@app/features/app/hooks/usePressable';
import styles from '@app/features/guild/components/modals/guild_tabs/MemberListStyles.module.css';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {useLingui} from '@lingui/react/macro';
import {CaretRightIcon, DotsThreeVerticalIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback} from 'react';

interface UserData {
	id: string;
	username: string;
	avatar?: string | null;
}
interface UserListItemProps {
	user: UserData;
	avatarUrl?: string;
	displayName?: string;
	tag?: string;
	nameColor?: string;
	isMobile: boolean;
	isMenuActive?: boolean;
	trailingContent?: React.ReactNode;
	onContextMenu: (event: React.MouseEvent<HTMLElement>, fromButton?: boolean) => void;
	onActivate?: () => void;
	onLongPress?: () => void;
}
export const UserListItem: React.FC<UserListItemProps> = observer(
	({
		user,
		avatarUrl,
		displayName,
		tag,
		nameColor,
		isMobile,
		isMenuActive = false,
		trailingContent,
		onContextMenu,
		onActivate,
		onLongPress,
	}) => {
		const {t} = useLingui();
		const {isPressed, pressableProps} = usePressable({disabled: !isMobile});
		const handleActivate = useCallback(() => {
			if (isMobile && onActivate) {
				onActivate();
			}
		}, [isMobile, onActivate]);
		const handleContextMenu = useCallback(
			(e: React.MouseEvent<HTMLElement>) => {
				if (!isMobile) {
					e.preventDefault();
					onContextMenu(e);
				}
			},
			[isMobile, onContextMenu],
		);
		const handleLongPress = useCallback(() => {
			onLongPress?.();
		}, [onLongPress]);
		const sharedContent = (
			<>
				<div className={styles.memberMain}>
					<div className={styles.avatarWrapper}>
						{avatarUrl ? (
							<img src={avatarUrl} alt="" className={styles.avatar} loading="lazy" />
						) : (
							<div className={styles.avatarPlaceholder}>{(displayName || user.username)[0].toUpperCase()}</div>
						)}
					</div>
					<div className={styles.memberInfo}>
						<div className={styles.nameRow}>
							<span className={styles.displayName} style={nameColor ? {color: nameColor} : undefined}>
								{displayName || user.username}
							</span>
							{trailingContent}
						</div>
						{tag && <span className={styles.tag}>{tag}</span>}
					</div>
				</div>
				{isMobile ? (
					<CaretRightIcon weight="bold" size={20} className={styles.chevron} />
				) : (
					<div className={styles.memberActions}>
						<Tooltip text={t`More Options`}>
							<button
								type="button"
								className={clsx(styles.moreButton, isMenuActive && styles.moreButtonActive)}
								onClick={(e) => {
									e.stopPropagation();
									onContextMenu(e, true);
								}}
							>
								<DotsThreeVerticalIcon weight="bold" className={styles.moreButtonIcon} />
							</button>
						</Tooltip>
					</div>
				)}
			</>
		);
		if (isMobile) {
			const content = (
				<button
					type="button"
					className={clsx(styles.memberItem, styles.memberItemInteractive, isPressed && styles.memberItemPressed)}
					onClick={handleActivate}
					onContextMenu={handleContextMenu}
					{...pressableProps}
				>
					{sharedContent}
				</button>
			);
			if (onLongPress) {
				return (
					<LongPressable onLongPress={handleLongPress} delay={500} pressedClassName={styles.memberItemPressed}>
						{content}
					</LongPressable>
				);
			}
			return content;
		}
		return (
			<div className={styles.memberItemWrapper}>
				<div
					role="group"
					className={styles.memberItem}
					data-non-interactive="true"
					onContextMenu={(e) => onContextMenu(e, false)}
				>
					{sharedContent}
				</div>
			</div>
		);
	},
);
